import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { 
  DocumentTextIcon,
  PlusIcon,
  TrashIcon,
  EyeIcon,
  CloudArrowUpIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  BuildingOfficeIcon,
  UserIcon,
  DocumentArrowDownIcon,
  PaperClipIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import api from '../../services/api';
import axios from 'axios'; // TODO: rimuovere, usare api
import API_CONFIG from '../../config/api';
import GestioneGaranti from './GestioneGaranti';
import { downloadAutenticato, fetchAnteprima } from '../../utils/downloadFile';

const TabDocumenti = forwardRef(({ 
  pratica, 
  ruoloUtente, 
  formatDate,
  onDocumentiAggiornati 
}, ref) => {
  const [documenti, setDocumenti] = useState([]);
  const [loadingDocumenti, setLoadingDocumenti] = useState(false);
  const [uploadingDocumento, setUploadingDocumento] = useState(false);
  const [documentiRichiesti, setDocumentiRichiesti] = useState([]);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  // Anteprima documento in modale
  const [anteprima, setAnteprima] = useState(null); // { blobUrl, type, nomeFile }
  const [anteprimaLoading, setAnteprimaLoading] = useState(false);

  // Apre l'anteprima del documento in una modale (inline, autenticata)
  const apriAnteprima = async (documento) => {
    setAnteprimaLoading(true);
    try {
      const { blobUrl, type } = await fetchAnteprima(
        `/api/pratica/${pratica.id}/documenti/${documento.id}/download`
      );
      setAnteprima({
        id: documento.id,
        blobUrl,
        type: type || documento.mime_type || '',
        nomeFile: documento.nome_file,
      });
    } catch (e) {
      toast.error(e.message || 'Errore nell\'anteprima del documento');
    } finally {
      setAnteprimaLoading(false);
    }
  };

  // Chiude la modale e libera il blob URL
  const chiudiAnteprima = () => {
    setAnteprima((prev) => {
      if (prev?.blobUrl) window.URL.revokeObjectURL(prev.blobUrl);
      return null;
    });
  };

  // Cleanup del blob URL quando il componente viene smontato
  useEffect(() => {
    return () => {
      if (anteprima?.blobUrl) window.URL.revokeObjectURL(anteprima.blobUrl);
    };
  }, [anteprima]);

  useEffect(() => {
    if (pratica?.id) {
      fetchDocumenti();
      fetchDocumentiRichiesti();
    }
  }, [pratica?.id]);

  const fetchDocumenti = async () => {
    setLoadingDocumenti(true);
    try {
// console.log(`📄 Caricamento documenti per pratica ${pratica.id}...`);
      const response = await api.get(`/pratica/${pratica.id}/documenti`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
// console.log(`📄 Documenti caricati:`, response.data);
      setDocumenti(response.data || []);
    } catch (error) {
      console.error('Errore durante il caricamento documenti:', error);
      toast.error('Errore nel caricamento dei documenti');
      setDocumenti([]);
    } finally {
      setLoadingDocumenti(false);
    }
  };

  const fetchDocumentiRichiesti = async () => {
    try {
// console.log(`📄 Caricamento documenti richiesti per pratica ${pratica.id}...`);
      const response = await api.get(`/pratica/${pratica.id}/documenti-richiesti`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
// console.log(`📄 Documenti richiesti:`, response.data);
      if (response.data.success) {
        setDocumentiRichiesti(response.data.documentiRichiesti || []);
      } else {
        setDocumentiRichiesti([]);
      }
    } catch (error) {
      console.error('Errore nel caricamento documenti richiesti:', error);
      setDocumentiRichiesti([]);
    }
  };

  const uploadDocumento = async (file, descrizioneDocumento, isTemplate = false) => {
    try {
      setUploadingDocumento(true);
// console.log(`📤 Upload documento: ${file.name}`);
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('tipo', 'documento_cliente');
      formData.append('descrizione', isTemplate ? 
        `${descrizioneDocumento} - ${file.name}` : 
        `Documento: ${file.name}`
      );

      const response = await api.post(`/pratica/${pratica.id}/documenti`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

// console.log(`📤 Documento caricato:`, response.data);
      if (response.data) {
        toast.success('Documento caricato con successo');
        fetchDocumenti();
        if (onDocumentiAggiornati) onDocumentiAggiornati();
      }
    } catch (error) {
      console.error('Errore durante il caricamento del documento:', error);
      toast.error('Errore durante il caricamento del documento');
    } finally {
      setUploadingDocumento(false);
    }
  };

  const downloadTemplate = async (templatePath, fileName) => {
    try {
      const response = await api.get(`/configurazione/documenti/template/download`, {
        params: { path: templatePath },
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Template scaricato con successo');
    } catch (error) {
      console.error('Errore nel download del template:', error);
      toast.error('Errore nel download del template');
    }
  };

  const eliminaDocumento = async (documentoId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo documento?')) {
      return;
    }

    try {
// console.log(`🗑️ Eliminazione documento ${documentoId}`);
      const response = await api.delete(`/pratica/${pratica.id}/documenti/${documentoId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

// console.log(`🗑️ Risposta eliminazione:`, response.data);
      // Controlla sia il formato con success che la presenza di message
      if (response.data.success || response.data.message || response.status === 200) {
        toast.success('Documento eliminato con successo');
        fetchDocumenti();
        if (onDocumentiAggiornati) onDocumentiAggiornati();
      }
    } catch (error) {
      console.error('Errore durante l\'eliminazione del documento:', error);
      toast.error('Errore durante l\'eliminazione del documento');
    }
  };

  const handleCaricaDocumento = (descrizioneDocumento) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (file) {
        await uploadDocumento(file, descrizioneDocumento, true);
      }
    };
    input.click();
  };

  const handleFileUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = '.pdf';
    input.onchange = async (e) => {
      const files = Array.from(e.target.files);
      for (const file of files) {
        await uploadDocumento(file, 'Documento generico', false);
      }
    };
    input.click();
  };

  const canUploadDocuments = () => {
    return (ruoloUtente === 'agente' || ruoloUtente === 'agente_commerciale' || ruoloUtente === 'agenzia') && 
           ['attesa_documenti_cliente', 'richiesta_integrazione', 'in_lavorazione'].includes(pratica.statoPratica);
  };

  const canDeleteDocuments = () => {
    return (ruoloUtente === 'agente' || ruoloUtente === 'agente_commerciale' || ruoloUtente === 'agenzia') && 
           ['attesa_documenti_cliente', 'richiesta_integrazione', 'in_lavorazione'].includes(pratica.statoPratica);
  };

  const isDocumentoCaricato = (descrizioneDocumento) => {
    return documenti.some(doc => 
      doc.descrizione && doc.descrizione.toLowerCase().includes(descrizioneDocumento.toLowerCase())
    );
  };

  // Verifica se tutti i documenti obbligatori sono stati caricati
  const verificaDocumentiObbligatori = () => {
    if (documentiRichiesti.length === 0) {
      return { 
        tuttiCaricati: true, 
        mancanti: [], 
        messaggio: 'Nessun documento richiesto configurato' 
      };
    }

    const documentiObbligatori = documentiRichiesti.filter(doc => !doc.opzionale);
    const mancanti = [];

    documentiObbligatori.forEach(docRichiesto => {
      const documentoCaricato = documenti.find(doc => 
        doc.descrizione && 
        doc.descrizione.toLowerCase().includes(docRichiesto.descrizione.toLowerCase())
      );
      
      if (!documentoCaricato) {
        mancanti.push(docRichiesto.descrizione);
      }
    });

    return {
      tuttiCaricati: mancanti.length === 0,
      mancanti,
      totaleObbligatori: documentiObbligatori.length,
      caricatiObbligatori: documentiObbligatori.length - mancanti.length,
      messaggio: mancanti.length === 0 
        ? 'Tutti i documenti obbligatori sono stati caricati'
        : `Mancano ${mancanti.length} documenti obbligatori: ${mancanti.join(', ')}`
    };
  };

  // Hook imperativo per l'uso da parte del componente padre
  useImperativeHandle(ref, () => ({
    verificaDocumentiObbligatori,
    isAllDocumentsLoaded: () => verificaDocumentiObbligatori().tuttiCaricati,
    getDocumentiMancanti: () => verificaDocumentiObbligatori().mancanti
  }));

  if (!pratica) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6 text-center">
        <p className="text-gray-500">Nessuna pratica selezionata</p>
      </div>
    );
  }

  const showTemplate = canUploadDocuments() && documentiRichiesti.length > 0;

  return (
    <div className="space-y-6">
      {/* Checklist Documenti Richiesti - Solo se può uploadare e ci sono documenti richiesti */}
      {showTemplate && (
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="px-6 py-4 border-b bg-gray-50">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <DocumentTextIcon className="h-5 w-5 mr-2 text-blue-600" />
                  Documenti Richiesti
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Documenti necessari per {pratica.cliente?.tipo_cliente || 'questo cliente'}
                </p>
                {/* Indicatore progresso documenti obbligatori */}
                {pratica.statoPratica === 'attesa_documenti_cliente' && (() => {
                  const statusDoc = verificaDocumentiObbligatori();
                  return (
                    <div className="mt-3">
                      <div className={`flex items-center space-x-2 p-3 rounded-lg ${
                        statusDoc.tuttiCaricati 
                          ? 'bg-green-50 border border-green-200' 
                          : 'bg-orange-50 border border-orange-200'
                      }`}>
                        {statusDoc.tuttiCaricati ? (
                          <CheckCircleIcon className="h-5 w-5 text-green-500" />
                        ) : (
                          <ExclamationTriangleIcon className="h-5 w-5 text-orange-500" />
                        )}
                        <div className="flex-1">
                          <p className={`text-sm font-medium ${
                            statusDoc.tuttiCaricati ? 'text-green-700' : 'text-orange-700'
                          }`}>
                            {statusDoc.messaggio}
                          </p>
                          {!statusDoc.tuttiCaricati && statusDoc.totaleObbligatori > 0 && (
                            <div className={`text-xs mt-1 ${
                              statusDoc.tuttiCaricati ? 'text-green-600' : 'text-orange-600'
                            }`}>
                              Progresso: {statusDoc.caricatiObbligatori}/{statusDoc.totaleObbligatori} documenti obbligatori
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
          
          <div className="p-6">
            {loadingTemplate ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                <p className="mt-4 text-gray-500">Caricamento documenti richiesti...</p>
              </div>
            ) : documentiRichiesti.length > 0 ? (
              <>
                {/* Documenti Base */}
                {documentiRichiesti.filter(d => d.categoria === 'base').length > 0 && (
                  <div className="mb-6">
                    <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                      <DocumentTextIcon className="h-4 w-4 mr-2 text-gray-600" />
                      Documenti Base
                    </h4>
                    <div className="space-y-2">
                      {documentiRichiesti.filter(d => d.categoria === 'base').map((documento, index) => (
                        <div key={`base-${index}`} 
                             className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                               documento.opzionale ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'
                             }`}>
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              {isDocumentoCaricato(documento.descrizione) ? (
                                <CheckCircleIcon className="h-5 w-5 text-green-500" />
                              ) : (
                                <div className={`h-5 w-5 rounded-full border-2 ${
                                  documento.opzionale ? 'border-blue-400' : 'border-gray-400'
                                }`} />
                              )}
                              <span className={`text-sm font-medium ${
                                documento.opzionale ? 'text-blue-700' : 'text-gray-700'
                              }`}>
                                {documento.descrizione}
                              </span>
                              {documento.opzionale && (
                                <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
                                  Opzionale
                                </span>
                              )}
                            </div>
                            {documento.template && (
                              <div className="flex items-center mt-1 text-xs text-blue-600">
                                <PaperClipIcon className="h-3 w-3 mr-1" />
                                <span>Template disponibile: {documento.template.fileName}</span>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center space-x-2">
                            {documento.template && (
                              <button
                                onClick={() => downloadTemplate(documento.template.path, documento.template.fileName)}
                                className="text-blue-600 hover:text-blue-800 p-1 rounded"
                                title="Scarica template"
                              >
                                <DocumentArrowDownIcon className="h-4 w-4" />
                              </button>
                            )}
                            <button
                              onClick={() => handleCaricaDocumento(documento.descrizione)}
                              className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors"
                              disabled={uploadingDocumento}
                            >
                              {uploadingDocumento ? 'Caricando...' : 'Carica'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Documenti Società Specifica */}
                {documentiRichiesti.filter(d => d.categoria === 'societa').length > 0 && (
                  <div className="mb-6">
                    <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                      <BuildingOfficeIcon className="h-4 w-4 mr-2 text-gray-600" />
                      Documenti Società
                    </h4>
                    <div className="space-y-2">
                      {documentiRichiesti.filter(d => d.categoria === 'societa').map((documento, index) => (
                        <div key={`societa-${index}`} 
                             className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                               documento.opzionale ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'
                             }`}>
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              {isDocumentoCaricato(documento.descrizione) ? (
                                <CheckCircleIcon className="h-5 w-5 text-green-500" />
                              ) : (
                                <div className={`h-5 w-5 rounded-full border-2 ${
                                  documento.opzionale ? 'border-blue-400' : 'border-gray-400'
                                }`} />
                              )}
                              <span className={`text-sm font-medium ${
                                documento.opzionale ? 'text-blue-700' : 'text-gray-700'
                              }`}>
                                {documento.descrizione}
                              </span>
                              {documento.opzionale && (
                                <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
                                  Opzionale
                                </span>
                              )}
                            </div>
                            {documento.template && (
                              <div className="flex items-center mt-1 text-xs text-blue-600">
                                <PaperClipIcon className="h-3 w-3 mr-1" />
                                <span>Template disponibile: {documento.template.fileName}</span>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center space-x-2">
                            {documento.template && (
                              <button
                                onClick={() => downloadTemplate(documento.template.path, documento.template.fileName)}
                                className="text-blue-600 hover:text-blue-800 p-1 rounded"
                                title="Scarica template"
                              >
                                <DocumentArrowDownIcon className="h-4 w-4" />
                              </button>
                            )}
                            <button
                              onClick={() => handleCaricaDocumento(documento.descrizione)}
                              className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors"
                              disabled={uploadingDocumento}
                            >
                              {uploadingDocumento ? 'Caricando...' : 'Carica'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8">
                <ExclamationTriangleIcon className="h-8 w-8 text-yellow-400 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">
                  Nessun documento richiesto configurato per questo tipo di cliente
                </p>
                <p className="text-gray-400 text-xs mt-1">
                  Contatta l'amministratore per configurare i documenti
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Gestione Documenti Caricati */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b bg-gray-50">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <DocumentTextIcon className="h-5 w-5 mr-2 text-blue-600" />
              Documenti Caricati
            </h3>
            {canUploadDocuments() && (
              <button
                onClick={handleFileUpload}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
                disabled={uploadingDocumento}
              >
                {uploadingDocumento ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                ) : (
                  <CloudArrowUpIcon className="h-4 w-4 mr-2" />
                )}
                Carica Documenti Liberi
              </button>
            )}
          </div>
        </div>
        
        <div className="p-6">
          {loadingDocumenti ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
              <p className="mt-4 text-gray-500">Caricamento documenti...</p>
            </div>
          ) : documenti.length > 0 ? (
            <div className="space-y-4">
              {documenti.map((documento) => (
                <div key={documento.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">{documento.nome_file}</h4>
                      <p className="text-sm text-gray-600 mt-1">{documento.descrizione}</p>
                      <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                        <span>Tipo: <span className="font-medium">{documento.tipo}</span></span>
                        <span>Caricato: <span className="font-medium">{formatDate(documento.data_caricamento)}</span></span>
                        <span>Dimensione: <span className="font-medium">{(documento.dimensione_file / 1024).toFixed(1)} KB</span></span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => apriAnteprima(documento)}
                        disabled={anteprimaLoading}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded disabled:opacity-50"
                        title="Visualizza documento"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => downloadAutenticato(`/api/pratica/${pratica.id}/documenti/${documento.id}/download`, documento.nome_file).catch((e) => toast.error(e.message || 'Errore nel download del documento'))}
                        className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors"
                      >
                        Scarica
                      </button>
                      {canDeleteDocuments() && (
                        <button
                          onClick={() => eliminaDocumento(documento.id)}
                          className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700 transition-colors"
                        >
                          Elimina
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6">
                <DocumentTextIcon className="h-12 w-12 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Nessun documento presente</h3>
              <p className="text-gray-600 mb-8 max-w-md mx-auto">
                {canUploadDocuments()
                  ? 'Carica i documenti del cliente per procedere con la pratica.'
                  : 'I documenti del cliente verranno visualizzati qui una volta caricati.'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Sezione Gestione Garanti */}
      <GestioneGaranti 
        pratica={pratica}
        ruoloUtente={ruoloUtente}
        onGarantiAggiornati={onDocumentiAggiornati}
      />

      {/* Modale anteprima documento */}
      {anteprima && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 p-4"
          onClick={chiudiAnteprima}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="text-sm font-medium text-gray-900 truncate pr-4" title={anteprima.nomeFile}>
                {anteprima.nomeFile}
              </h3>
              <div className="flex items-center space-x-2 flex-shrink-0">
                <button
                  onClick={() => downloadAutenticato(`/api/pratica/${pratica.id}/documenti/${anteprima.id}/download`, anteprima.nomeFile).catch((e) => toast.error(e.message || 'Errore nel download'))}
                  className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors"
                >
                  Scarica
                </button>
                <button
                  onClick={chiudiAnteprima}
                  className="p-1 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded"
                  title="Chiudi"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Contenuto */}
            <div className="flex-1 bg-gray-100 overflow-auto flex items-center justify-center">
              {anteprima.type.startsWith('image/') ? (
                <img
                  src={anteprima.blobUrl}
                  alt={anteprima.nomeFile}
                  className="max-w-full max-h-full object-contain"
                />
              ) : anteprima.type === 'application/pdf' ? (
                <iframe
                  src={anteprima.blobUrl}
                  title={anteprima.nomeFile}
                  className="w-full h-full border-0"
                />
              ) : (
                <div className="text-center p-8">
                  <p className="text-gray-600 mb-4">
                    Anteprima non disponibile per questo tipo di file.
                  </p>
                  <button
                    onClick={() => downloadAutenticato(`/api/pratica/${pratica.id}/documenti/${anteprima.id}/download`, anteprima.nomeFile).catch((e) => toast.error(e.message || 'Errore nel download'))}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                  >
                    Scarica il documento
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default TabDocumenti; 